import { supabase } from '@/lib/supabase';
import { getKoreanDay, getDayName } from '@/lib/time';
import type { RestaurantWithProducts } from './restaurant.types';

export class RestaurantQueryService {

  // 마감할인 탭 전용: 제품 + 오퍼(마감할인) + 오늘 재고를 최소 필드만 조회하여 반환
  async getDeadlineRestaurantsWithOffers(): Promise<{ success: boolean; data?: RestaurantWithProducts[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select(`
          *,
          products:products(
            id,
            name,
            original_price,
            image_url,
            is_available,
            offers:product_offers!inner(
            id,
            offer_type,
            discount_rate,
            discounted_price,
            is_active,
            valid_from,
            valid_until,
            deadline_pickup_start_time,
            deadline_pickup_end_time,
            deadline_order_start_time,
            deadline_order_end_time
          ),
            stock:product_stock(
              date,
              current_stock
            )
          )
        `)
        .eq('is_open', true) // 기본적으로 오픈 매장만
        .eq('is_test_store', false)
        .eq('products.offers.offer_type', 'DEADLINE_DISCOUNT')
        .eq('products.offers.is_active', true);

      if (error) {
        console.error('Get deadline restaurants error:', error);
        return { success: false, error: error.message };
      }

      const today = new Date().toISOString().split('T')[0];
      const now = new Date();

      const processed = (data || []).map((restaurant: any) => {
        const products = (restaurant.products || []).map((product: any) => {
          // 오늘 재고만 필터링
          const todayStock = (product.stock || []).find((s: any) => s.date === today);

          // 마감할인 오퍼만 필터링 + 유효기간 검증
          const deadlineOffers = (product.offers || []).filter((offer: any) => {
            if (offer.offer_type !== 'DEADLINE_DISCOUNT' || !offer.is_active) return false;
            if (offer.valid_from) {
              const offerDate = offer.valid_from.includes('T') ? offer.valid_from.split('T')[0] : offer.valid_from;
              if (offerDate !== today) return false;
            }
            if (offer.valid_until) {
              const validUntil = new Date(offer.valid_until);
              if (!Number.isNaN(validUntil.getTime()) && now > validUntil) return false;
            }
            return true;
          });

          return {
            ...product,
            offers: deadlineOffers,
            stock: todayStock ? [todayStock] : []
          };
        }).filter((p: any) => (p.offers || []).length > 0);

        return {
          ...restaurant,
          products
        };
      });

      return { success: true, data: processed };
    } catch (error) {
      console.error('Get deadline restaurants with offers error:', error);
      return { success: false, error: '마감할인 매장을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  async getRestaurants(params?: {
    latitude?: number;
    longitude?: number;
    radius?: number;
    is_open?: boolean;
    withProducts?: boolean;
    offerType?: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  }): Promise<{ success: boolean; data?: RestaurantWithProducts[]; error?: string }> {
    try {
      // Use the new RPC function that includes product count
      const { data, error } = await supabase.rpc('get_restaurants_with_product_count');

      if (error) {
        console.error('Get restaurants error:', error);
        return { success: false, error: error.message };
      }

      // RPC 함수가 반환하는 실제 구조에서 데이터 추출
      // RPC returns: { get_restaurants_with_product_count: [...] }
      const rawData = data?.get_restaurants_with_product_count || data || [];

      // 테스트 매장 제외
      const filteredData = (rawData as RestaurantWithProducts[]).filter(r => (r as any).is_test_store !== true);

      // 현재 요일의 영업시간 정보를 추가하고 비즈니스 로직 적용
      const processedData = (filteredData as RestaurantWithProducts[])?.map(restaurant => {
        // Ensure operating_hours is always preserved and properly typed
        let operatingHoursData: any[] = [];
        
        // Handle different data structures that might come from RPC
        if (Array.isArray((restaurant as any).operating_hours)) {
          operatingHoursData = (restaurant as any).operating_hours;
        } else if ((restaurant as any).operating_hours) {
          // If it's an object, try to convert it to array
          operatingHoursData = Object.values((restaurant as any).operating_hours);
        } else {
          // Fallback to empty array to prevent undefined
          operatingHoursData = [];
          console.warn(`Missing operating_hours for restaurant ${restaurant.name}, using empty array`);
        }

        const todayOperatingHours = this.getTodayOperatingHoursFromArray(operatingHoursData);
        
        const restaurantWithHours = {
          ...restaurant,
          operating_hours: operatingHoursData,  // 원본 배열 확실히 보존
          todayOperatingHours,
          // Preserve product_count for debugging
          product_count: (restaurant as any).product_count
        };

        // Apply business logic if products are requested and offer type is specified
        if (params?.withProducts && params?.offerType) {
          return this.processRestaurantAvailability(restaurantWithHours, params.offerType);
        }

        return restaurantWithHours;
      }) || [];

      return { success: true, data: processedData };
    } catch (error) {
      console.error('Get restaurants error:', error);
      return { success: false, error: '가게 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  async getRestaurantById(
    id: string, 
    withProducts: boolean = false
  ): Promise<{ success: boolean; data?: RestaurantWithProducts; error?: string }> {
    try {
      let query = supabase
        .from('restaurants')
        .select(`
          *,
          operating_hours:restaurant_operating_hours(
            day_of_week,
            is_closed,
            open_time,
            close_time,
            break_start_time,
            break_end_time
          ),
          business:businesses(id, business_name, business_number, representative_name, is_verified)${withProducts ? `,
          products(
            id,
            name,
            description,
            category,
            original_price,
            image_url,
            ingredients,
            allergens,
            calories,
            is_available,
            offers:product_offers!inner(
              id,
              offer_type,
              discount_rate,
              discounted_price,
              min_discount_rate,
              is_random_set,
              pickup_start_time,
              pickup_end_time,
              max_quantity_per_order,
              is_active,
              is_current,
              valid_from,
              valid_until,
              version,
              deadline_pickup_start_time,
              deadline_pickup_end_time,
              deadline_order_start_time,
              deadline_order_end_time
            ),
            stock:product_stock(
              date,
              initial_stock,
              current_stock,
              sold_stock
            )
          )` : ''}
        `)
        .eq('id', id)
        .single();

      const { data, error } = await query;

      if (error) {
        console.error('Get restaurant error:', error);
        return { success: false, error: error.message };
      }

      // Ensure operating_hours is always properly preserved
      if (data && (data as any).operating_hours) {
        let operatingHoursData: any[] = [];
        
        if (Array.isArray((data as any).operating_hours)) {
          operatingHoursData = (data as any).operating_hours;
        } else if (typeof (data as any).operating_hours === 'object') {
          operatingHoursData = Object.values((data as any).operating_hours);
        }
        
        // Preserve original operating_hours array structure
        (data as any).operating_hours = operatingHoursData;
      }

      return { success: true, data: data as unknown as RestaurantWithProducts };
    } catch (error) {
      console.error('Get restaurant error:', error);
      return { success: false, error: '가게 정보를 불러오는 중 오류가 발생했습니다.' };
    }
  }

  async getDeadlineDiscountRestaurants(
    _userLocation?: { lat: number; lng: number }
  ): Promise<RestaurantWithProducts[]> {
    try {
      // Get all restaurants with deadline discount offers (business logic applied)
      const result = await this.getRestaurants({
        withProducts: true,
        offerType: 'DEADLINE_DISCOUNT'
      });

      if (!result.success || !result.data) {
        return [];
      }

      // TODO: Add distance filtering if userLocation is provided
      return result.data;
    } catch (error) {
      console.error('Get deadline discount restaurants error:', error);
      return [];
    }
  }

  async getReservationDiscountRestaurants(
    _userLocation?: { lat: number; lng: number }
  ): Promise<RestaurantWithProducts[]> {
    try {
      // Get all restaurants with reservation discount offers (business logic applied)
      const result = await this.getRestaurants({
        withProducts: true,
        offerType: 'RESERVATION_DISCOUNT'
      });

      if (!result.success || !result.data) {
        return [];
      }

      // TODO: Add distance filtering if userLocation is provided
      return result.data;
    } catch (error) {
      console.error('Get reservation discount restaurants error:', error);
      return [];
    }
  }

  async getRegularOrderRestaurants(
    _userLocation?: { lat: number; lng: number }
  ): Promise<RestaurantWithProducts[]> {
    try {
      // Get all restaurants with regular order options (business logic applied)
      const result = await this.getRestaurants({
        withProducts: true,
        offerType: 'REGULAR_ORDER'
      });

      if (!result.success || !result.data) {
        return [];
      }

      // TODO: Add distance filtering if userLocation is provided
      return result.data;
    } catch (error) {
      console.error('Get regular order restaurants error:', error);
      return [];
    }
  }

  /**
   * 현재 요일의 영업시간 정보를 추출하는 메서드
   */
  // 새로운 테이블 구조용 메서드
  private getTodayOperatingHoursFromArray(operatingHours: any[]): {
    dayName: string;
    openTime?: string;
    closeTime?: string;
    breakStartTime?: string;
    breakEndTime?: string;
    isClosed: boolean;
  } {
    const today = getKoreanDay();
    const dayName = getDayName(today);
    
    // 오늘 요일에 해당하는 영업시간 찾기
    const todayHours = operatingHours.find(hours => hours.day_of_week === today);
    
    if (!todayHours) {
      // 기본값 반환
      return {
        dayName,
        openTime: undefined,
        closeTime: undefined,
        breakStartTime: undefined,
        breakEndTime: undefined,
        isClosed: true
      };
    }
    
    return {
      dayName,
      openTime: todayHours.open_time || undefined,
      closeTime: todayHours.close_time || undefined,
      breakStartTime: todayHours.break_start_time || undefined,
      breakEndTime: todayHours.break_end_time || undefined,
      isClosed: todayHours.is_closed || false
    };
  }

  /**
   * 비즈니스 로직: 매장의 주문 가능 상태를 처리하는 메서드
   * 사용자 요구사항에 따른 구현:
   * - 마감할인: 오늘 날짜의 재고가 있어야 활성화
   * - 일반주문/예약할인: 최신 재고가 있고 유효한 영업시간이면 활성화
   */
  private processRestaurantAvailability(
    restaurant: RestaurantWithProducts, 
    orderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER'
  ): RestaurantWithProducts {
    const today = new Date().toISOString().split('T')[0];
    
    if (!restaurant.products || restaurant.products.length === 0) {
      // 상품이 없는 매장은 비활성화 상태로 표시
      return {
        ...restaurant,
        products: [],
        // Add availability flag for UI
        isAvailable: false
      } as RestaurantWithProducts;
    }

    const processedProducts = restaurant.products.map(product => {
      if (!product.offers || product.offers.length === 0) {
        return { ...product, isAvailable: false };
      }

      // Filter offers by order type
      const relevantOffers = product.offers.filter(offer => offer.offer_type === orderType);
      if (relevantOffers.length === 0) {
        return { ...product, isAvailable: false };
      }

      // Check stock availability based on order type
      let hasValidStock = false;
      
      if (orderType === 'DEADLINE_DISCOUNT') {
        // 마감할인: 오늘 날짜의 재고가 필요
        hasValidStock = product.stock?.some(stock => 
          stock.date === today && stock.current_stock > 0
        ) || false;
      } else {
        // 일반주문/예약할인: 최신 재고가 있으면 됨
        if (product.stock && product.stock.length > 0) {
          // Sort by date descending to get latest stock
          const latestStock = product.stock.sort((a, b) => b.date.localeCompare(a.date))[0];
          hasValidStock = latestStock.current_stock > 0;
        }
      }

      // Check if offers are active
      const hasActiveOffers = relevantOffers.some(offer => {
        // Basic active check
        if (!offer.is_active) return false;
        
        // For DEADLINE_DISCOUNT, ensure offer was registered today
        if (orderType === 'DEADLINE_DISCOUNT') {
          // Check if valid_from is today
          if (offer.valid_from) {
            const validFromDate = new Date(offer.valid_from).toISOString().split('T')[0];
            if (validFromDate !== today) return false;
          }
          // If valid_from is null, consider it as today's offer (legacy compatibility)
          
          // Check if offer hasn't expired
          if (offer.valid_until) {
            const now = new Date();
            const validUntil = new Date(offer.valid_until);
            if (now > validUntil) return false;
          }
        } else {
          // For RESERVATION_DISCOUNT and REGULAR_ORDER, use basic validity check
          if (offer.valid_from || offer.valid_until) {
            const now = new Date();
            const validFrom = offer.valid_from ? new Date(offer.valid_from) : null;
            const validUntil = offer.valid_until ? new Date(offer.valid_until) : null;
            
            if (validFrom && now < validFrom) return false;
            if (validUntil && now > validUntil) return false;
          }
        }
        
        return true;
      });

      return {
        ...product,
        offers: relevantOffers,
        isAvailable: hasValidStock && hasActiveOffers
      };
    });

    // Restaurant is available if it has at least one available product
    const isRestaurantAvailable = processedProducts.some(product => product.isAvailable);

    return {
      ...restaurant,
      products: processedProducts,
      isAvailable: isRestaurantAvailable
    } as RestaurantWithProducts;
  }
}

export const restaurantQueryService = new RestaurantQueryService();
